import { CHIP_NOT_AGENCY, CHIP_NO_OPEN_OPP, GS_PRESET_IDS } from '@shared/flow/account-view';
import {
  CHIP_CONTACT_ACCOUNT_NO_OPEN_OPP,
  CHIP_CONTACT_ACCOUNT_NOT_AGENCY,
  CHIP_NO_RECENT_QUEUE_SEND,
} from '@shared/flow/contact-view';
import type { FilterGroup } from '@shared/flow/filter';
import { buildPlayMemberMineGroup } from '@shared/flow/play-people';
import type { FlowIdentity } from '@shared/flow/identity';
import { ROLE_ME_CHIP, meChipGroup } from '@shared/flow/role-me-chip';
import type { FlowView } from '@shared/flow/view';

/**
 * Role-based landing defaults for Command Center's account/contact tables
 * (product spec: role-based default filters — see the table in the
 * implementation plan). Seeded by each mount's `adaptOnOpen` through
 * `withRoleDefaults()` below on every preset and draft open (see
 * docs/flow/custom-views-notes.md, "Seeding rule"), except the two Growth
 * Strategist presets, which define their whole filter. MERGED
 * alongside whatever the view already filters on rather than replacing it,
 * and never onto a saved view. Fully clearable: the seeded rows are ordinary
 * visible filter groups, and because the adapted view is also the dirty
 * BASELINE, landing shows no "Modified" banner while clearing a seeded chip
 * correctly does.
 *
 * | role   | account default                                  | contact default (global mount) | contact default (account mount) | contact default (plays roster surface) | contact default (Not in this play yet + the two member sections) |
 * |--------|---------------------------------------------------|----------------------------------|-----------------------------------|--------------------------------|-----------------------------------|
 * | claydr | claydr_name = me  + not-agency + no-open-opps      | account_claydr_name = me + not-agency + no-open-opps + LIM excludes "Meeting Booked" & "Inbound Email" | (none) | no-recent-queue-send | Mine (owner / ClayDR / GS / actioned-by = me) |
 * | gtme   | owner_name = me                                    | account_owner_name = me          | (none) | no-recent-queue-send | Mine |
 * | gs     | gs_name = me                                       | gs_name = me                     | (none) | no-recent-queue-send | Mine |
 * | null   | (none — show all)                                  | (none — status quo)              | (none) | no-recent-queue-send | Mine |
 *
 * On the CONTACT table the ClayDR/GTME chips filter on the account-level
 * ownership fields (`account_claydr_name` / `account_owner_name`) because
 * ownership lives on the account (2026-10-16); chip ids stay stable so a
 * rep's stored chip is deduped, not doubled. GS stays `gs_name`.
 *
 * "Not in this play yet" (`surface: 'play-candidates'`) used to share the
 * plays roster's role-independent L30 chip (`CHIP_NO_RECENT_QUEUE_SEND`).
 * PM simplification (2026-09-25, supersedes
 * plans/2026-09-25-004-plays-not-in-play-defaults-plan.md's outer-OR Group
 * A/Group B design): it now seeds the SAME removable Mine group the two
 * member sections already seed (`buildPlayMemberMineGroup()`), not the L30
 * chip. Still role-independent (checked before the `!identity` guard) and
 * still an ordinary visible/removable/Reset-restored filter group — only
 * WHICH default changed.
 *
 * Two ORTHOGONAL inputs, both contacts-only, both derived once by
 * `useContactMount` (plan 2026-09-10-001 D9) and forwarded verbatim:
 *  - `mount`: 'global' = the Command Center contact table; 'account' = the
 *    account drill-down roster (and the main-Terra account page's Contacts
 *    tab, which mounts the same component). An account roster is already
 *    scoped to one account, so it must NOT get ownership scoping on top
 *    (claydr_name = me would hide that account's contacts a rep isn't the
 *    ClayDR for) — that has never seeded on account mounts.
 *  - `surface`: 'people' (default), 'plays' (the roster), 'play-candidates'
 *    (the Plays tab's "Not in this play yet" section, plan 2026-09-24-005
 *    U16), or one of the two member sections 'play-responded' /
 *    'play-not-responded' (plan 2026-09-25-002 U6) — which product surface
 *    the same component is mounted under. This replaced the old third mount
 *    kind `'play'` (plan 2026-09-09-003): the Plays tab is a surface, not a
 *    mount.
 *
 * "Not in this play yet" and the two MEMBER sections (plan 2026-09-25-002
 * D2, joined by 'play-candidates' 2026-09-25 — PM simplification, supersedes
 * plans/2026-09-25-004-plays-not-in-play-defaults-plan.md's outer-OR Group
 * A/Group B design) seed the removable Mine group (`buildPlayMemberMineGroup`:
 * owner / ClayDR / GS / actioned-by = `{me}`, OR-combined) — role-independent
 * like the plays roster's L30 chip, checked before the `!identity` guard. It
 * replaced the one-shot "Go to Responded (Mine)" transient chip on the
 * member sections: an ordinary seed, in working AND baseline, restored by
 * Reset. None of these three seed the L30 chip — a member/candidate is not
 * necessarily "actionable right now" the way the whole roster's readiness
 * filter means; Mine narrows by whose book it is instead.
 *
 * The plays ROSTER surface (`surface: 'plays'`) follows a DIFFERENT rule
 * than either mount: it is role-INDEPENDENT (every role, including no-role,
 * gets the same one default), checked before the `!identity` guard (D9). A
 * play's roster is the CAMPAIGN's people — everyone the play was sent to —
 * not the rep's book, so ownership scoping (`claydr_name = me` / `owner_name
 * = me`) would silently hide teammates' contacts from a play's own tab,
 * which is wrong for a shared campaign view. The one default that DOES
 * apply, `CHIP_NO_RECENT_QUEUE_SEND` ("no queue send in the last 30 days"),
 * is a recency/readiness filter, not an ownership one — it lands the roster
 * on "who's actionable right now" rather than "who's mine" — and like every
 * other seeded chip it is a visible, clearable filter group, never hidden
 * scope. The ACCOUNT entity has no plays surface at all (the Plays tab
 * mounts only the contact component), so `surface` is contacts-only.
 *
 * REVERSED 2026-08-27 (Annie Hale drill-down feedback, R1(a) of the
 * bucket-a-e-followup plan): the not-agency / no-open-opp / LIM-exclusion
 * chips used to ALSO seed on the account mount (Spencer, 2026-07-31 —
 * "make sure the contact filters also persist in the account drilldown … for
 * default contact filters"; and 2026-08-12 — bring the contact-view ClayDR
 * defaults up to parity with the account-view ones). Reps reported those
 * chips reading as permanent, un-resettable "carry-over" on every drill-down
 * — they were baked into both the working state AND the dirty baseline, so
 * no "Modified" banner ever showed and Reset couldn't remove them. Decision:
 * an account mount now seeds NOTHING for any role, full stop; the global CC
 * contact table keeps the complete claydr default set unchanged (including
 * claydr_name = me, which was never part of the account-mount set to begin
 * with).
 *
 * "= me" above is now LITERAL: the ownership conditions carry the `{me}` token
 * (`@shared/flow/filter`), not the viewer's resolved `sfDisplayName` (U5 / R2).
 * The token is resolved server-side against the ACTING user — so
 * `?impersonate=` narrows to the impersonated rep — and rewritten into an
 * indexed `salesforce_user.id` equality by the person-field pre-pass
 * (src/services/flow/me-token.ts). Three consequences worth keeping in mind
 * when editing this file:
 *   - the chip is portable: saving one into a shared view (U6) resolves per
 *     VIEWER, where a baked-in name would freeze the author's book;
 *   - it must sit on a PERSON-typed field (`claydr_name` / `owner_name` /
 *     `gs_name`) — the pre-pass only rewrites those, and a `{me}` anywhere
 *     else reaches SQL as the literal string;
 *   - `sfDisplayName` is now display-only (the `[Me] → Casey ClayDR` viewer
 *     resolution in the filter UI); it is deliberately NOT read here.
 * The group ids (`chip-my-claydr` / `chip-my-owner` / `chip-my-gs`) are stable:
 * they are React keys, and `meEmptyState`'s [Clear] targets a chip by id.
 * (U6 resolved the deferred question about `mergePresetFilters()`, which used
 * to key view-switch carry-over off these ids: that function is DELETED —
 * opening a view now loads its full definition, and the role default is
 * re-applied by each mount's `adaptOnOpen` on every preset/draft open.)
 */
const CHIP_CONTACT_NOT_MEETING_BOOKED: FilterGroup = {
  // Exclude contacts whose Last Interesting Moment is a booked meeting —
  // ClayDRs prospect net-new, not chase already-booked meetings — AND
  // contacts whose LIM is an inbound email (Spencer, 2026-08-12: additive
  // ask, no stated rationale beyond "exclude this too" — both conditions sit
  // in the SAME AND group so they compose as one exclusion, not two separate
  // clearable rows). `not_contains` matches "Meeting Booked - …" / "Inbound
  // Email - …" LIM names and keeps null-LIM rows (condition-to-sql: NOT
  // ILIKE … OR IS NULL). Clearable, like every default chip. The group id
  // stays `chip-not-meeting-booked` (unchanged) — it's a React key referenced
  // by roleDefaultSeeding.test.tsx; only a second condition was added.
  id: 'chip-not-meeting-booked',
  logic: 'AND',
  conditions: [
    { id: 'chip-not-meeting-booked-1', field: 'lim_name', operator: 'not_contains', value: 'Meeting Booked' },
    { id: 'chip-not-meeting-booked-2', field: 'lim_name', operator: 'not_contains', value: 'Inbound Email' },
  ],
};

export interface RoleDefaultOpts {
  mount?: 'global' | 'account';
  /**
   * Contacts only. `'plays'` (the roster, D9) seeds the L30 chip for every
   * role. `'play-candidates'` ("Not in this play yet", 2026-09-25 PM
   * simplification) and the two member sections `'play-responded'` /
   * `'play-not-responded'` (plan 2026-09-25-002 U6) all seed the same
   * removable Mine group instead (`buildPlayMemberMineGroup`).
   */
  surface?: 'people' | 'plays' | 'play-candidates' | 'play-responded' | 'play-not-responded';
}

export function buildRoleDefaultFilter(
  identity: FlowIdentity | null,
  entity: 'account' | 'contact',
  opts?: RoleDefaultOpts,
): FilterGroup[] {
  // The plays ROSTER surface is role-INDEPENDENT (D9) — checked before the
  // `!identity` guard below, because a no-role viewer still gets the L30 chip
  // on a play's contact roster (see the matrix comment above).
  if (entity === 'contact' && opts?.surface === 'plays') return [CHIP_NO_RECENT_QUEUE_SEND];
  // "Not in this play yet" (2026-09-25 PM simplification — supersedes plan
  // 004's outer-OR Group A/Group B design) and the two member sections
  // (plan 2026-09-25-002 D2) — same role-independent shape, Mine instead of
  // L30 (see the module doc).
  if (entity === 'contact' && (opts?.surface === 'play-candidates' || opts?.surface === 'play-responded' || opts?.surface === 'play-not-responded')) return [buildPlayMemberMineGroup()];

  if (!identity) return [];
  // `sfDisplayName` is intentionally unread — see the `{me}` note above.
  const { flowRole } = identity;
  const mount = opts?.mount ?? 'global';
  // An identity whose role is outside the three Flow roles at runtime (an
  // admin) seeds nothing, on either table.
  if (!Object.prototype.hasOwnProperty.call(ROLE_ME_CHIP, flowRole)) return [];

  if (entity === 'account') {
    if (flowRole === 'claydr') return [meChipGroup('claydr', 'account'), CHIP_NOT_AGENCY, CHIP_NO_OPEN_OPP];
    return [meChipGroup(flowRole, 'account')];
  }

  // entity === 'contact' — an ACCOUNT mount seeds NOTHING, for every role.
  // Reversed 2026-08-27 (Annie Hale drill-down feedback, R1(a)): the
  // not-agency/no-open-opp/meeting-booked chips used to also seed there, and
  // read to reps as permanent, un-resettable carry-over (baked into both the
  // working state AND the dirty baseline, so no "Modified" banner ever showed
  // and Reset couldn't remove them) — see the module doc comment above.
  //
  // ONE guard rather than a per-role early return: "account mounts don't seed"
  // is a property of the mount, not of any role, so a role added below cannot
  // forget it.
  if (mount === 'account') return [];

  // Every branch below is therefore the GLOBAL contact table's default set.
  if (flowRole === 'claydr') {
    return [
      meChipGroup('claydr', 'contact'),
      CHIP_CONTACT_ACCOUNT_NO_OPEN_OPP,
      CHIP_CONTACT_ACCOUNT_NOT_AGENCY,
      CHIP_CONTACT_NOT_MEETING_BOOKED,
    ];
  }
  return [meChipGroup(flowRole, 'contact')];
}

/**
 * The ONE seeding implementation both mounts call from `adaptOnOpen` (D1).
 *
 * The two Growth Strategist presets are returned unchanged. Every other view is
 * seeded as follows.
 *
 * Merge semantics, decided 2026-08-11 (spec: "Seeding rule"):
 *  - role-default groups are APPENDED as their own groups, so the view's own
 *    conditions are never mutated, reordered or deduplicated — a preset with
 *    its own predicate (By Last Interesting Moment) keeps it AND gains the
 *    rep's book scoping, instead of silently going company-wide the way it
 *    did while seeding was gated on an empty filter;
 *  - group ids stay stable (`chip-my-claydr`, …) — they are React keys and
 *    `meEmptyState`'s [Clear] targets a chip by id. Duplicate keys would
 *    break the filter list, and the rep already has that row on screen.
 *    Terra adds a rep's "my book" chip to a preset only if that chip is not
 *    there already, and it tells by the chip's id. GS My Book is built from
 *    that same chip. So when a strategist moves from GS My Book to another
 *    preset and their filters come along, Terra sees the chip is already
 *    there and does not add it a second time;
 *  - the view object is returned UNCHANGED (same reference) when there is
 *    nothing to add, so a no-role viewer's presets are untouched.
 *
 * Callers must not apply this to a SAVED view — a saved view owns its
 * complete predicate (the `kind === 'saved'` early return in each mount).
 */
export function withRoleDefaults(
  view: FlowView,
  identity: FlowIdentity | null,
  entity: 'account' | 'contact',
  opts?: RoleDefaultOpts,
): FlowView {
  // A Growth Strategist preset is complete as written. Role defaults seed only
  // onto the shared presets and drafts.
  if (GS_PRESET_IDS.has(view.id)) return view;
  const roleGroups = buildRoleDefaultFilter(identity, entity, opts);
  if (roleGroups.length === 0) return view;
  const present = new Set(view.filter.map(g => g.id));
  const additions = roleGroups.filter(g => !present.has(g.id));
  if (additions.length === 0) return view;
  return { ...view, filter: [...view.filter, ...additions] };
}
